/**
 * WitnessSuitePanel — the Bridge Witness Suite (eight witness types).
 *
 * Renders the eight canonical witness types with their CSK theorem,
 * categorical type, neutralization mode, and structural description
 * (RD-27 §3).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import type { MonographDataTableColumn } from "@csk/demo-ui";
import witnessesData from "../data/witnesses.json" with { type: "json" };
type WitnessRow = (typeof witnessesData.witnesses)[number];
const MODE_COLOR: Record<string, string> = {
  IMMUNE: panelTokens.immune,
  CONTAINED: panelTokens.contained,
  INERT: panelTokens.inert,
};
const COLUMNS: readonly MonographDataTableColumn<WitnessRow>[] = [
  {
    id: "witness",
    header: "Witness",
    accessorKey: "id",
    cell: ({ row }) => (
      <span className="font-mono font-bold text-[var(--abk-bright)]">
        {row.original.id}
      </span>
    ),
  },
  {
    id: "theorem",
    header: "CSK Theorem",
    accessorKey: "cskTheorem",
    cell: ({ row }) => (
      <span className="font-mono text-[var(--abk-accent)]">
        {row.original.cskTheorem}
      </span>
    ),
  },
  {
    id: "type",
    header: "Categorical Type",
    accessorKey: "type",
  },
  {
    id: "mode",
    header: "Mode",
    accessorKey: "mode",
    cell: ({ row }) =>
      PanelSurface.badge(
        row.original.mode,
        MODE_COLOR[row.original.mode] ?? panelTokens.muted,
      ),
  },
];
export function WitnessSuitePanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="The Bridge Witness Suite"
      subtitle="Eight canonical witness types — each a direct consequence of one or more CSK neutralization theorems."
      accentColor={panelTokens.accentPurple}
    >
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          The Bridge Witness Suite (BWS) is complete: every POS-A proof
          obligation is dischargeable by exactly one bridge witness (ABK-1
          witness-completeness). Each witness is a categorical object — a slice
          morphism, a graded-monad token, a linear consumption token, a
          fibration lift, or a separability witness — whose absence makes the
          corresponding crossing structurally inexpressible.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        The eight witness types
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.DataTable
          ariaLabel="Bridge Witness Suite — eight witness types"
          data={witnessesData.witnesses}
          columns={COLUMNS}
          getRowId={(row) => row.id}
          compact
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        Structural properties
      </PanelSurface.SectionHeading>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-3">
        {witnessesData.witnesses.map((w) => {
          const color = MODE_COLOR[w.mode] ?? panelTokens.accent;
          return (
            <PanelSurface.TintedRow
              key={w.id}
              color={color}
              heading={w.id}
              trailing={
                <>
                  {PanelSurface.badge(w.cskTheorem, panelTokens.accent)}
                  {PanelSurface.badge(w.mode, color)}
                </>
              }
            >
              <PanelSurface.SectionLabel className="font-mono normal-case">
                {w.type}
              </PanelSurface.SectionLabel>
              {w.description}
            </PanelSurface.TintedRow>
          );
        })}
      </div>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-27 §3 Bridge Witness Suite",
            path: "research/settled/RD-27-AGENTIC-BOUNDARY-KERNEL.md",
          },
          {
            label: "BridgeWitnessSuite.agda",
            path: "agda/Research/AgenticBoundaryKernel/BridgeWitnessSuite.agda",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
